import React, { useState } from 'react';
import { 
  TrendingUp, TrendingDown, BarChart3, Calendar,
  DollarSign, Package, Users, ArrowUp, ArrowDown
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface PlanogramPattern {
  id: string;
  name: string;
  description: string;
  expectedSalesIncrease: number;
  expectedProfitIncrease: number;
  changes: number;
  confidence: number;
  rules: string[];
}

interface EffectTrackerProps {
  patterns: PlanogramPattern[];
  shelfEfficiency: number;
}

interface PerformanceMetric {
  name: string;
  current: number;
  previous: number;
  target: number;
  unit: string;
}

interface TimeSeriesData {
  date: string;
  sales: number;
  profit: number;
  traffic: number;
  conversion: number;
}

const EffectTracker: React.FC<EffectTrackerProps> = ({ patterns, shelfEfficiency }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter'>('week');
  const [selectedPattern, setSelectedPattern] = useState<string>(patterns[0]?.id || '');
  
  // パフォーマンスメトリクス（モック）
  const metrics: PerformanceMetric[] = [
    { name: '売上高', current: 158000, previous: 135000, target: 155000, unit: '円' },
    { name: '粗利率', current: 26.3, previous: 22.5, target: 25.0, unit: '%' },
    { name: '在庫回転率', current: 12.5, previous: 10.2, target: 12.0, unit: '回' },
    { name: '客単価', current: 2850, previous: 2600, target: 2800, unit: '円' },
    { name: '購買点数', current: 4.2, previous: 3.8, target: 4.0, unit: '点' },
    { name: '欠品率', current: 3.2, previous: 5.8, target: 3.0, unit: '%' }
  ];
  
  // 時系列データ生成（モック）
  const generateTimeSeriesData = (): TimeSeriesData[] => {
    const days = selectedPeriod === 'week' ? 7 : selectedPeriod === 'month' ? 30 : 90;
    const data: TimeSeriesData[] = [];
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() - days);
    
    for (let i = 0; i < days; i++) {
      const date = new Date(baseDate);
      date.setDate(date.getDate() + i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        sales: 120000 + Math.random() * 40000 + (i * 500),
        profit: 25000 + Math.random() * 10000 + (i * 100),
        traffic: 800 + Math.random() * 200,
        conversion: 3.5 + Math.random() * 1.5
      });
    }
    
    return data;
  };
  
  const timeSeriesData = generateTimeSeriesData();
  const latestData = timeSeriesData[timeSeriesData.length - 1] || { sales: 0, profit: 0, traffic: 0, conversion: 0 };
  const previousData = timeSeriesData[timeSeriesData.length - 8] || { sales: 0, profit: 0, traffic: 0, conversion: 0 };
  
  // 週次比較計算（ゼロ除算を回避）
  const weeklyComparison = {
    sales: previousData.sales > 0 ? ((latestData.sales - previousData.sales) / previousData.sales * 100).toFixed(1) : '0.0',
    profit: previousData.profit > 0 ? ((latestData.profit - previousData.profit) / previousData.profit * 100).toFixed(1) : '0.0',
    traffic: previousData.traffic > 0 ? ((latestData.traffic - previousData.traffic) / previousData.traffic * 100).toFixed(1) : '0.0',
    conversion: previousData.conversion > 0 ? ((latestData.conversion - previousData.conversion) / previousData.conversion * 100).toFixed(1) : '0.0'
  };
  
  // チャートの高さ計算
  const maxSales = Math.max(...timeSeriesData.map(d => d.sales));
  const chartHeight = 200;

  return (
    <div className="space-y-6">
      {/* 期間選択とパターン選択 */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {(['week', 'month', 'quarter'] as const).map(period => (
            <Button
              key={period}
              variant={selectedPeriod === period ? 'primary' : 'secondary'}
              size="small"
              onClick={() => setSelectedPeriod(period)}
            >
              {period === 'week' ? '週次' : period === 'month' ? '月次' : '四半期'}
            </Button>
          ))}
        </div>
        
        <select 
          className="px-3 py-2 text-sm border rounded-lg"
          style={{ borderColor: colors.ui.border }}
          value={selectedPattern}
          onChange={(e) => setSelectedPattern(e.target.value)}
        >
          {patterns.map(pattern => (
            <option key={pattern.id} value={pattern.id}>{pattern.name}</option>
          ))}
        </select>
      </div>
      
      {/* KPIサマリー */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign size={20} style={{ color: colors.support.success }} />
            <Badge variant={Number(weeklyComparison.sales) > 0 ? 'success' : 'error'}>
              {Number(weeklyComparison.sales) > 0 ? '+' : ''}{weeklyComparison.sales}%
            </Badge>
          </div>
          <div className="text-2xl font-bold mb-1" style={{ color: colors.text.primary }}>
            ¥{(latestData.sales / 1000).toFixed(0)}K
          </div>
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            日次売上
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp size={20} style={{ color: colors.accent.blue }} />
            <Badge variant={Number(weeklyComparison.profit) > 0 ? 'success' : 'error'}>
              {Number(weeklyComparison.profit) > 0 ? '+' : ''}{weeklyComparison.profit}%
            </Badge>
          </div>
          <div className="text-2xl font-bold mb-1" style={{ color: colors.text.primary }}>
            ¥{(latestData.profit / 1000).toFixed(0)}K
          </div>
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            日次粗利
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <Users size={20} style={{ color: colors.accent.purple }} />
            <Badge variant={Number(weeklyComparison.traffic) > 0 ? 'success' : 'error'}>
              {Number(weeklyComparison.traffic) > 0 ? '+' : ''}{weeklyComparison.traffic}%
            </Badge>
          </div>
          <div className="text-2xl font-bold mb-1" style={{ color: colors.text.primary }}>
            {Math.round(latestData.traffic)}人
          </div>
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            来店客数
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <Package size={20} style={{ color: colors.status.warning }} />
            <Badge variant={Number(weeklyComparison.conversion) > 0 ? 'success' : 'error'}>
              {Number(weeklyComparison.conversion) > 0 ? '+' : ''}{weeklyComparison.conversion}%
            </Badge>
          </div>
          <div className="text-2xl font-bold mb-1" style={{ color: colors.text.primary }}>
            {latestData.conversion.toFixed(1)}%
          </div>
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            購買率
          </div>
        </Card>
      </div>
      
      {/* パフォーマンストレンド */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
          売上トレンド
        </h3>
        
        <div className="relative" style={{ height: chartHeight }}>
          {/* Y軸ラベル */}
          <div className="absolute -left-12 top-0 h-full flex flex-col justify-between text-xs" 
            style={{ color: colors.text.secondary }}
          >
            <span>¥{(maxSales / 1000).toFixed(0)}K</span>
            <span>¥{(maxSales * 0.75 / 1000).toFixed(0)}K</span>
            <span>¥{(maxSales * 0.5 / 1000).toFixed(0)}K</span>
            <span>¥{(maxSales * 0.25 / 1000).toFixed(0)}K</span>
            <span>¥0</span>
          </div>
          
          {/* グリッド線 */}
          {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
            <div
              key={ratio}
              className="absolute w-full border-t"
              style={{
                borderColor: colors.ui.border,
                bottom: `${ratio * 100}%`,
                borderStyle: 'dashed'
              }}
            />
          ))}
          
          {/* バーチャート */}
          <div className="flex items-end justify-around h-full">
            {timeSeriesData.slice(-7).map((data, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full max-w-8 bg-blue-500 rounded-t hover:bg-blue-600 transition-all cursor-pointer"
                  style={{
                    height: `${(data.sales / maxSales) * 100}%`,
                    backgroundColor: colors.accent.blue
                  }}
                  title={`売上: ¥${(data.sales / 1000).toFixed(0)}K`}
                />
                <span className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                  {new Date(data.date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Card>
      
      {/* メトリクス詳細 */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            パフォーマンス指標
          </h3>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            {metrics.map(metric => {
              const change = ((metric.current - metric.previous) / metric.previous * 100);
              const isPositive = metric.name === '欠品率' ? change < 0 : change > 0;
              const achievementRate = (metric.current / metric.target * 100);
              
              return (
                <div key={metric.name} className="border rounded-lg p-4" style={{ borderColor: colors.ui.border }}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium" style={{ color: colors.text.primary }}>
                      {metric.name}
                    </h4>
                    <div className="flex items-center gap-1">
                      {isPositive ? (
                        <ArrowUp size={16} style={{ color: colors.support.success }} />
                      ) : (
                        <ArrowDown size={16} style={{ color: colors.status.error }} />
                      )}
                      <span className="text-sm font-medium" style={{ 
                        color: isPositive ? colors.support.success : colors.status.error 
                      }}>
                        {change > 0 ? '+' : ''}{change.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span style={{ color: colors.text.secondary }}>現在</span>
                      <div className="font-bold text-lg">
                        {metric.current.toLocaleString()}{metric.unit}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: colors.text.secondary }}>前週</span>
                      <div className="font-medium">
                        {metric.previous.toLocaleString()}{metric.unit}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: colors.text.secondary }}>目標</span>
                      <div className="font-medium">
                        {metric.target.toLocaleString()}{metric.unit}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: colors.text.secondary }}>達成率</span>
                      <span className="font-medium">
                        {achievementRate.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full transition-all"
                        style={{ 
                          width: `${Math.min(100, achievementRate)}%`,
                          backgroundColor: achievementRate >= 100 ? colors.support.success :
                                        achievementRate >= 80 ? colors.status.warning :
                                        colors.status.error
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      
      {/* レポート生成 */}
      <Card className="p-4 border-l-4" style={{ borderLeftColor: colors.accent.purple }}>
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-semibold mb-2" style={{ color: colors.text.primary }}>
              効果測定レポート
            </h4>
            <p className="text-sm mb-3" style={{ color: colors.text.secondary }}>
              選択したパターンの実施効果をレポートとして出力できます。
              経営層向けのサマリーから、詳細な分析データまで含まれます。
            </p>
            <div className="flex gap-2">
              <Button variant="primary">
                <BarChart3 size={16} />
                詳細レポート生成
              </Button>
              <Button variant="secondary">
                <Calendar size={16} />
                定期レポート設定
              </Button>
            </div>
          </div>
          <Badge variant="success">
            効果確認済み
          </Badge>
        </div>
      </Card>
    </div>
  );
};

export default EffectTracker;
